'use client';

import React, { memo, useEffect, useState, useRef } from 'react';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface IProps {
  src?: string;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  /** adapt : 9:16 비율 이외는 contain 처리 */
  fitType?: 'cover' | 'contain' | 'adapt';
  pb?: number;
  priority?: boolean;
  errorImg?: string;
  onLoadingComplete?: () => void;
  onClick?: () => void;
}

const defaultErrorImage =
  'https://static.wconcept.co.kr/display/images/onerror_img_product.png';

const Img = ({
  src = '',
  alt = '',
  width,
  height,
  pb,
  fitType = 'cover',
  className,
  priority = false,
  errorImg,
  onLoadingComplete,
  onClick,
}: IProps) => {
  const [$fitType, setFitType] = useState(fitType);
  const [imageSrc, setImageSrc] = useState<string>(priority ? src : '');
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setImageSrc(src);

    if ($fitType === 'adapt') {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        const w = img.width;
        const h = img.height;
        const aspectRatio = w / h;
        // console.log(`img loadstart 비율: `, w, h, aspectRatio);

        if (Math.abs(aspectRatio - 9 / 16) > 0.01) {
          // console.log('img loadstart 9:16 비율이 아닙니다.');
          setFitType('contain');
        } else {
          setFitType('cover');
        }
      };
    }
  }, [src, $fitType]);

  const handleError = () => {
    setIsLoading(false);
    setImageSrc(errorImg || defaultErrorImage);
  };

  const handleLoad = () => {
    setIsLoading(false);
    onLoadingComplete?.();
  };

  const param = {
    className: classNameBind(['img', className || '']),
    width,
    height,
    $pb: pb,
    onClick,
  };

  const imgParam: any = {
    ref: imgRef,
    src: imageSrc || errorImg || defaultErrorImage,
    alt: alt || '',
    $fitType,
    unoptimized: true,
    onError: handleError,
    $priority: priority,
  };

  if (width && height) {
    imgParam.width = width;
    imgParam.height = height;
  } else {
    // width와 height가 없으면 반응형으로 렌더링
    imgParam.fill = true;
    imgParam.sizes = '100vw';
    imgParam.style = {
      width: '100%',
    };
  }

  // priority 이미지에 대한 추가 최적화
  if (priority) {
    imgParam.fetchPriority = 'high'; // 브라우저 우선순위 힌트
    imgParam.loading = 'eager'; // 즉시 로딩
  } else {
    imgParam.loading = 'lazy'; // 지연 로딩
  }

  return (
    <S.Box {...param}>
      <S.LazyImg {...imgParam} onLoad={handleLoad} />
    </S.Box>
  );
};

export default memo(Img);
